// LivestreamDetailOffline.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import WithLivestreamRating from '../container/WithLivestreamRating.js';

import UniversalComponent from '../component/UniversalComponent.jsx';
import Rating from '../component/Rating.jsx';

import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color, headerHeight } from '../style/variables.js';

export const LivestreamDetailOffline = ({
  streamId = null,
  isExclusivePrivateLayout = false,
}) => {
  const { t } = useTranslation();
  return (
    <StyledLivestreamDetailOffline>
      <Header>
        {t(
          isExclusivePrivateLayout
            ? 'title_in_exclusive_stream'
            : 'title_stream_finished',
          {
            ns: TranslationNamespace.LIVESTREAM,
          }
        )}
      </Header>
      <Body>
        <OfflineUserInfo>
          {isExclusivePrivateLayout ? (
            <UniversalComponent
              filename="Container_UserFeedItem"
              importer={
                /* #__LOADABLE__ */ () => import('../container/UserFeedItem.js')
              }
              id={streamId}
              size={100}
            />
          ) : (
            <UniversalComponent
              filename="Container_LiveStreamUser"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LiveStreamUser.js')
              }
              userId={streamId}
              streamId={streamId}
              offline
            />
          )}
        </OfflineUserInfo>
        <WithLivestreamRating id={streamId}>
          {({
            ratingStepSize,
            displayRating,
            displayRatingCount,
            rating,
            isDisplayRatingEnabled,
          }) =>
            !!rating && isDisplayRatingEnabled ? (
              <RatingSummary>
                <DisplayRating>{displayRating}</DisplayRating>
                <Rating
                  value={displayRating || 0}
                  max={100 / ratingStepSize}
                  iconSize={20}
                  isReadOnly
                />
                <DisplayRatingCount>{displayRatingCount}</DisplayRatingCount>
              </RatingSummary>
            ) : null
          }
        </WithLivestreamRating>
      </Body>
      <Footer>
        {isExclusivePrivateLayout ? (
          <UniversalComponent
            filename="Container_ExclusivePrivateEndNotificationToggle"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/ExclusivePrivateEndNotificationToggle.js')
            }
            streamId={streamId}
          />
        ) : (
          <UniversalComponent
            filename="Container_UserProfileFooter"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/UserProfileFooter.js')
            }
            userId={streamId}
            positionType={'relative'}
          />
        )}
        <UniversalComponent
          filename="Container_LiveStreamRow"
          userId={streamId}
          importer={
            /* #__LOADABLE__ */ () => import('../container/LiveStreamRow.js')
          }
        />
      </Footer>
    </StyledLivestreamDetailOffline>
  );
};

LivestreamDetailOffline.propTypes = {
  streamId: PropTypes.string,
  isExclusivePrivateLayout: PropTypes.bool,
};

const StyledLivestreamDetailOffline = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 0px 16px;
  height: ${headerHeight}px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  user-select: none;
  font-size: 16px;
  font-weight: 600;
  color: ${color.neutral[100]};
`;

const Body = styled.div`
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const OfflineUserInfo = styled.div``;

const RatingSummary = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DisplayRating = styled.div`
  margin-right: 4px;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const DisplayRatingCount = styled.div`
  margin-left: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral[75]};
  ::before {
    content: '(';
  }
  ::after {
    content: ')';
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  flex: none;
`;

export default LivestreamDetailOffline;
